

import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Alert, Divider, Chip, InputAdornment, CircularProgress,
  Tooltip, Stack
} from '@mui/material';
import {
  ArrowBack, Save, CheckCircle, LocalShipping, Receipt,
  Inventory, Info, Warning
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API;

const ReceiveGoods = () => {
  const { po_id } = useParams();
  const navigate = useNavigate();
  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [transportName, setTransportName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [qualityStatus, setQualityStatus] = useState('passed');
  const [qualityNotes, setQualityNotes] = useState('');
  const [receivingItems, setReceivingItems] = useState([]);
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [discount, setDiscount] = useState(0);

  /* CHANGED: track GRN result for batch summary */
  const [grnResult, setGrnResult] = useState(null);

  useEffect(() => { loadPO(); }, [po_id]);

  const loadPO = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/purchase-orders/${po_id}`);
      const poData = response.data;
      setPO(poData);

      const items = poData.items.map((item, idx) => {
        let medicineId = item.medicine_id || item.id || null;
        if (!medicineId && item._id) {
          medicineId = typeof item._id === 'object'
            ? item._id.$oid || String(item._id)
            : item._id;
        }

        return {
          medicine_id:        medicineId,
          medicine_name:      item.medicine_name || item.name || '',
          generic_name:       item.generic_name || '',
          manufacturer:       item.manufacturer || '',
          quantity_ordered:   item.quantity_ordered || 0,
          quantity_received:  0,
          quantity_pending:   item.quantity_pending || item.quantity_ordered || 0,
          batch_number:       '',
          expiry_date:        '',
          manufacturing_date: '',        /* CHANGED: always initialise */
          purchase_price:     item.unit_price || item.purchase_price_estimate || 0,
          mrp:                0,
          selling_price:      0,
        };
      });

      setReceivingItems(items);
    } catch (err) {
      setError('Failed to load purchase order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const handleItemChange = (index, field, value) => {
    const updated = [...receivingItems];
    updated[index][field] = value;

    /* CHANGED: auto-calculate selling_price as 90% of MRP (editable) */
    if (field === 'mrp' && parseFloat(value) > 0) {
      updated[index].selling_price = parseFloat((parseFloat(value) * 0.9).toFixed(2));
    }

    setReceivingItems(updated);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setGrnResult(null);

    if (!invoiceNumber.trim()) { setError('Invoice number is required'); return; }
    if (!invoiceDate)           { setError('Invoice date is required');   return; }

    const itemsToReceive = receivingItems.filter(i => i.quantity_received > 0);
    if (!itemsToReceive.length) {
      setError('Please enter quantity received for at least one item');
      return;
    }

    for (const item of itemsToReceive) {
      if (!item.medicine_id) {
        setError(`Medicine ID missing for ${item.medicine_name}. Contact support.`);
        return;
      }
      if (!item.batch_number.trim()) {
        setError(`Batch number required for ${item.medicine_name}`);
        return;
      }
      if (!item.expiry_date) {
        setError(`Expiry date required for ${item.medicine_name}`);
        return;
      }
      if (!item.mrp || item.mrp <= 0) {
        setError(`MRP required for ${item.medicine_name}`);
        return;
      }
      if (item.quantity_received > item.quantity_pending) {
        setError(`Quantity for ${item.medicine_name} exceeds pending (${item.quantity_pending})`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const grnData = {
        received_by:          localStorage.getItem('user_id') || 'system',
        invoice_number:       invoiceNumber,
        invoice_date:         invoiceDate,
        invoice_amount:       parseFloat(invoiceAmount) || 0,
        transport_name:       transportName,
        vehicle_number:       vehicleNumber,
        quality_check_status: qualityStatus,
        quality_check_notes:  qualityNotes,
        notes:                notes,
        items:                itemsToReceive,
        amount_paid:          parseFloat(amountPaid) || 0,
        payment_mode:         paymentMode,
        discount_percentage:  parseFloat(discount) || 0,
      };

      const response = await axios.post(
        `${API_BASE_URL}/purchase-orders/${po_id}/receive`,
        grnData
      );

      /* CHANGED: store result for batch summary panel */
      setGrnResult(response.data);
      setSuccess(
        `GRN ${response.data.grn?.grn_number || ''} created. ` +
        `${response.data.items_updated} medicine(s) updated with new batch data.`
      );

      setTimeout(() => navigate(`/purchase-orders/${po_id}`), 3000);

    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to receive goods.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── totals ───────────────────────────────────────────────────── */
  const calculateTotals = () => {
    let subtotal = 0;
    receivingItems.forEach(item => {
      if (item.quantity_received > 0) {
        subtotal += item.quantity_received * (item.purchase_price || 0);
      }
    });
    const discountAmount = (subtotal * (parseFloat(discount) || 0)) / 100;
    const afterDiscount  = subtotal - discountAmount;
    const gst            = afterDiscount * 0.12;
    const grandTotal     = afterDiscount + gst;
    return {
      subtotal,
      discount:     discountAmount,
      afterDiscount,
      gst,
      grandTotal,
      balance:      grandTotal - (parseFloat(amountPaid) || 0),
    };
  };

  const totals          = calculateTotals();
  const totalReceiving  = receivingItems.reduce((s, i) => s + (parseInt(i.quantity_received) || 0), 0);
  const itemsWithoutId  = receivingItems.filter(i => !i.medicine_id);
  const DRAWER_WIDTH    = '300px';

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!po) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Warning sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>Failed to Load PO</Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="outlined" onClick={() => navigate('/purchase-orders')}>Back</Button>
              <Button variant="contained" onClick={loadPO}>Retry</Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', ml: { xs: 0, md: DRAWER_WIDTH }, p: { xs: 2, sm: 3 } }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 10, bgcolor: 'white' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate(`/purchase-orders/${po_id}`)} sx={{ bgcolor: 'grey.100' }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" fontWeight={600}>Receive Goods</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">PO:</Typography>
                  <Chip label={po.po_number} size="small" sx={{ bgcolor: '#006D77', color: '#fff', fontWeight: 600 }} />
                  <Typography color="text.disabled">|</Typography>
                  <Typography variant="body2" color="text.secondary">{po.distributor_name}</Typography>
                </Stack>
              </Box>
            </Stack>
            <Button
              variant="contained"
              size="large"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSubmit}
              disabled={submitting || !!itemsWithoutId.length}
              sx={{ minWidth: 200, bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' } }}
            >
              {submitting ? 'Saving...' : 'Complete GRN'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 3 }}>

        {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>{success}</Alert>}

        {itemsWithoutId.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Data Error: {itemsWithoutId.length} item(s) missing medicine ID
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {itemsWithoutId.map((item, idx) => <li key={idx}>{item.medicine_name}</li>)}
            </ul>
          </Alert>
        )}

        {/* CHANGED: GRN result summary panel */}
        {grnResult && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              GRN Created — Batch Summary
            </Typography>
            {(grnResult.inventory_updates || []).map((u, idx) => (
              <Typography key={idx} variant="body2">
                {u.medicine_name} | batch: {u.batch} | added: {u.quantity_added} units |
                stock: {u.old_stock} → {u.new_total_stock}
              </Typography>
            ))}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>

            {/* ── Invoice Details ─────────────────────────────── */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 2 }}>
                    <Receipt sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Invoice Details</Typography>
                    <Typography variant="caption" color="text.secondary">Enter supplier invoice information</Typography>
                  </Box>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Invoice Number *" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth type="date" label="Invoice Date *" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth type="number" label="Invoice Amount" value={invoiceAmount} onChange={e => setInvoiceAmount(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ step: 0.01, min: 0 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth type="number" label="Discount (%)" value={discount} onChange={e => setDiscount(e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ step: 0.01, min: 0, max: 100 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth type="number" label="Amount Paid" value={amountPaid} onChange={e => setAmountPaid(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ step: 0.01, min: 0 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth select label="" value={paymentMode} onChange={e => setPaymentMode(e.target.value)} SelectProps={{ native: true }}>
                      <option value="">Select Payment Mode</option>
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                      <option value="cheque">Cheque</option>
                      <option value="credit">Credit</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth select label="Quality Check Status" value={qualityStatus} onChange={e => setQualityStatus(e.target.value)} SelectProps={{ native: true }}>
                      <option value="passed">✓ Passed</option>
                      <option value="partial">⚠ Partial Pass</option>
                      <option value="failed">✗ Failed</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Quality Check Notes" value={qualityNotes} onChange={e => setQualityNotes(e.target.value)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Transport ───────────────────────────────────── */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 2 }}>
                    <LocalShipping sx={{ color: 'success.main', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Transport Information</Typography>
                  </Box>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Transport Company" value={transportName} onChange={e => setTransportName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Vehicle Number" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value.toUpperCase())} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Additional Notes" value={notes} onChange={e => setNotes(e.target.value)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Items Table ─────────────────────────────────── */}
            <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 2 }}>
                      <Inventory sx={{ color: 'warning.main', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>Items to Receive</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Batch details update both the inventory ledger and the FEFO deduction system
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip label={`${receivingItems.length} Items`} color="primary" variant="outlined" />
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600, borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 600 } }}>
                        <TableCell sx={{ minWidth: 200 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ minWidth: 80 }}>Ordered</TableCell>
                        <TableCell align="center" sx={{ minWidth: 80 }}>Pending</TableCell>
                        <TableCell align="center" sx={{ minWidth: 100 }}>Receiving *</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>Batch # *</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Mfg Date</TableCell>   {/* CHANGED: uncommented */}
                        <TableCell sx={{ minWidth: 150 }}>Expiry Date *</TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>Purchase Price</TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          <Tooltip title="MRP on the medicine package — controls the batch MRP in Medicine record">
                            <span>MRP * ⓘ</span>
                          </Tooltip>
                        </TableCell>
                        {/* CHANGED: note that selling price is advisory only */}
                        <TableCell align="right" sx={{ minWidth: 140 }}>
                          <Tooltip title="Stored in audit ledger. Live selling price is computed by AYUJA engine from base price + revenue %.">
                            <span>Selling Price ⓘ</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receivingItems.map((item, index) => {
                        const isReceiving  = item.quantity_received > 0;
                        const hasMedicineId = !!item.medicine_id;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': { bgcolor: 'grey.50' },
                              bgcolor:    isReceiving ? 'primary.50' : 'inherit',
                              borderLeft: !hasMedicineId ? '4px solid #ef4444' : 'none',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {item.medicine_name}
                                {!hasMedicineId && (
                                  <Chip label="NO ID" size="small" color="error" sx={{ ml: 1, fontSize: '0.65rem' }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.generic_name || 'No generic name'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={item.quantity_ordered} size="small" sx={{ bgcolor: 'grey.100' }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={item.quantity_pending} size="small" color={item.quantity_pending > 0 ? 'warning' : 'default'} />
                            </TableCell>
                            <TableCell align="center">
                              <TextField type="number" size="small" value={item.quantity_received}
                                onChange={e => handleItemChange(index, 'quantity_received', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: item.quantity_pending, style: { textAlign: 'center' } }}
                                disabled={!hasMedicineId} sx={{ width: 90 }} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" value={item.batch_number}
                                onChange={e => handleItemChange(index, 'batch_number', e.target.value)}
                                placeholder="Batch #" disabled={!isReceiving || !hasMedicineId} sx={{ width: 120 }} />
                            </TableCell>
                            {/* CHANGED: manufacturing date column restored */}
                            <TableCell>
                              <TextField type="date" size="small" value={item.manufacturing_date}
                                onChange={e => handleItemChange(index, 'manufacturing_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: today }}
                                disabled={!isReceiving || !hasMedicineId} sx={{ width: 145 }} />
                            </TableCell>
                            <TableCell>
                              <TextField type="date" size="small" value={item.expiry_date}
                                onChange={e => handleItemChange(index, 'expiry_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: today }}
                                disabled={!isReceiving || !hasMedicineId} sx={{ width: 145 }} />
                            </TableCell>
                            <TableCell align="right">
                              <TextField type="number" size="small" value={item.purchase_price}
                                onChange={e => handleItemChange(index, 'purchase_price', parseFloat(e.target.value) || 0)}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ step: 0.01, min: 0 }}
                                disabled={!isReceiving || !hasMedicineId} sx={{ width: 115 }} />
                            </TableCell>
                            <TableCell align="right">
                              <TextField type="number" size="small" value={item.mrp}
                                onChange={e => handleItemChange(index, 'mrp', parseFloat(e.target.value) || 0)}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ step: 0.01, min: 0 }}
                                disabled={!isReceiving || !hasMedicineId} sx={{ width: 115 }} />
                            </TableCell>
                            <TableCell align="right">
                              <TextField type="number" size="small" value={item.selling_price}
                                onChange={e => handleItemChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ step: 0.01, min: 0 }}
                                disabled={!isReceiving || !hasMedicineId} sx={{ width: 115 }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    MRP and Purchase Price update the medicine's batch record (used for FEFO stock deduction).
                    Selling price is stored in the audit ledger only — live selling price is managed by the AYUJA pricing engine.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Summary sidebar ──────────────────────────────── */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ position: 'sticky', top: 90, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Receiving Summary</Typography>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ bgcolor: 'primary.50', borderRadius: 2, p: 2, border: 1, borderColor: 'primary.200' }}>
                    <Typography variant="caption" color="text.secondary">Total Units Receiving</Typography>
                    <Typography variant="h4" fontWeight={700} color="primary.main">{totalReceiving}</Typography>
                  </Box>
                  <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, border: 1, borderColor: 'grey.300' }}>
                    <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                    <Typography variant="h6" fontWeight={600}>₹{totals.subtotal.toFixed(2)}</Typography>
                  </Box>
                  {totals.discount > 0 && (
                    <Box sx={{ bgcolor: 'success.50', borderRadius: 2, p: 2, border: 1, borderColor: 'success.200' }}>
                      <Typography variant="caption" color="text.secondary">Discount ({discount}%)</Typography>
                      <Typography variant="body1" fontWeight={600} color="success.dark">-₹{totals.discount.toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ bgcolor: 'info.50', borderRadius: 2, p: 2, border: 1, borderColor: 'info.200' }}>
                    <Typography variant="caption" color="text.secondary">GST (12%)</Typography>
                    <Typography variant="h6" fontWeight={600} color="info.dark">₹{totals.gst.toFixed(2)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ bgcolor: 'warning.50', borderRadius: 2, p: 2, border: 1, borderColor: 'warning.200' }}>
                    <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                    <Typography variant="h5" fontWeight={700} color="warning.dark">₹{totals.grandTotal.toFixed(2)}</Typography>
                  </Box>
                  {parseFloat(amountPaid) > 0 && (
                    <Box sx={{ bgcolor: 'success.50', borderRadius: 2, p: 2, border: 1, borderColor: 'success.200' }}>
                      <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                      <Typography variant="h6" fontWeight={600} color="success.dark">
                        ₹{parseFloat(amountPaid).toFixed(2)}
                      </Typography>
                      {paymentMode && (
                        <Chip label={paymentMode.toUpperCase()} size="small" sx={{ mt: 1, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  )}
                  {totals.balance > 0 && (
                    <Box sx={{ bgcolor: 'error.50', borderRadius: 2, p: 2, border: 1, borderColor: 'error.200' }}>
                      <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                      <Typography variant="h6" fontWeight={600} color="error.dark">
                        ₹{totals.balance.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Purchase Order</Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {[
                    ['PO Number',    po.po_number],
                    ['Distributor',  po.distributor_name],
                    ['PO Date',      new Date(po.po_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })],
                  ].map(([label, val]) => (
                    <Stack key={label} direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Tooltip title={val}>
                        <Typography variant="body2" fontWeight={600}
                          sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {val}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  ))}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip label={po.status || 'Pending'} size="small" color="warning" fontWeight={600} />
                  </Stack>
                </Stack>

                <Divider sx={{ my: 3 }} />
                <Alert severity="warning" icon={<Info />}>
                  <Typography variant="caption">
                    Completing GRN updates both the inventory ledger and the FEFO batch system used for order fulfillment.
                  </Typography>
                </Alert>

                <Button fullWidth variant="contained" size="large"
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSubmit}
                  disabled={submitting || totalReceiving === 0 || !!itemsWithoutId.length}
                  sx={{ mt: 2, py: 1.5, bgcolor: '#006D77', '&:hover': { bgcolor: '#005A63' } }}>
                  {submitting ? 'Processing...' : 'Complete GRN'}
                </Button>

                {itemsWithoutId.length > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Cannot complete GRN: {itemsWithoutId.length} item(s) have missing medicine IDs
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ReceiveGoods;